import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useServiceTemplates } from '@/hooks/use-service-templates';
import { useAuthStore } from '@/stores/auth-store';
import { ServiceTemplateManager } from '@/components/services/service-template-manager';
import { Package, ChevronDown, Settings } from 'lucide-react';

interface ServiceItem {
  name: string;
  description?: string;
  unitPrice: number;
  category?: string;
}

interface CataloguePickerProps {
  onAddService: (service: ServiceItem) => void;
}

/**
 * Inline "Browse Catalogue" dropdown that lists service templates
 * grouped by category. Selecting one calls onAddService to add it
 * as a line item in a quote or invoice.
 */
export function CatalogueSection({ onAddService }: CataloguePickerProps) {
  const { data } = useServiceTemplates();
  const templates = data?.data || [];
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'admin' || role === 'project_manager';
  const [managerOpen, setManagerOpen] = useState(false);

  // Group by category
  const grouped: Record<string, typeof templates> = {};
  templates.forEach((t: any) => {
    if (!t.isActive) return;
    const cat = t.category || 'Uncategorised';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(t);
  });

  const categories = Object.keys(grouped);

  if (templates.length === 0) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="ghost" size="sm" className="gap-1 text-xs">
            <Package size={12} />
            Browse Catalogue
            <ChevronDown size={10} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72 max-h-72 overflow-y-auto">
          {categories.map((cat, catIdx) => (
            <div key={cat}>
              {catIdx > 0 && <DropdownMenuSeparator />}
              <div className="px-2 py-1 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
                {cat}
              </div>
              {grouped[cat].map((t: any) => (
                <DropdownMenuItem
                  key={t.id}
                  onClick={() =>
                    onAddService({
                      name: t.name,
                      description: t.description,
                      unitPrice: t.unitPrice,
                      category: t.category,
                    })
                  }
                  className="flex items-center justify-between gap-2 cursor-pointer"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{t.name}</p>
                    {t.description && (
                      <p className="text-[10px] text-[var(--text-secondary)] truncate">{t.description}</p>
                    )}
                  </div>
                  <span className="text-xs font-mono font-bold text-dyndev-green whitespace-nowrap">
                    £{t.unitPrice.toFixed(0)}
                  </span>
                </DropdownMenuItem>
              ))}
            </div>
          ))}

          {/* Quick-access for admins to manage the catalogue */}
          {isAdmin && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setManagerOpen(true)}
                className="gap-2 text-xs text-[var(--text-secondary)] cursor-pointer"
              >
                <Settings size={12} />
                Customise Catalogue
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Service Template Manager modal — rendered outside DropdownMenu to avoid portal nesting */}
      <ServiceTemplateManager open={managerOpen} onOpenChange={setManagerOpen} />
    </>
  );
}
